import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useIsMobileLayout } from '@src/hooks/useIsMobileLayout';
import { useUser } from '@src/hooks/useUser';
import { useTradingSettings } from '@src/providers/AppConfigProvider';
import { useExecutionReports } from '@src/providers/ExecutionReportsContext';
import {
  Box,
  bpsToPercent,
  Button,
  ButtonVariants,
  createBlotterTableNativeClickRef,
  FormControlSizes,
  SideEnum,
  Tooltip,
  useGlobalToasts,
  useSecuritiesContext,
  useSocketClient,
  type Quote,
} from '@talos/kyoko';
import { memo } from 'react';

import { useRFQTradingConfirmation } from '../../../../components/OMS/NewRFQ/otherUserNegotiatedRFQTradeConfirmation';
import type { QuoteMarketWithBestBidOffer } from '../../../../utils/rfq';
import { getQuoteTradeSideDisabledReason } from '../utils';
import { ACCEPT_BUTTON_STATE, useQuoteAcceptButtonState } from './useQuoteAcceptButtonState';

interface QuoteAcceptButtonProps {
  /** The full Quote entity */
  quote: Quote;
  /** Which side this button trades */
  side: SideEnum;
  /** The pre-computed best market for this side (bid for Sell, offer for Buy) */
  bestMarket: QuoteMarketWithBestBidOffer | undefined;
  careOrderSide?: SideEnum;
}

/**
 * A blotter action button that implements the double-click-to-trade pattern.
 *
 * First click: locks in the current best price.
 * Second click: sends the acceptQuote request.
 * Button shows a loading state until the quote reaches a terminal status.
 */
export const QuoteAcceptButton = memo(function QuoteAcceptButton({
  quote,
  side,
  bestMarket,
  careOrderSide,
}: QuoteAcceptButtonProps) {
  const user = useUser();
  const webSocketClient = useSocketClient();
  const { add: addToast } = useGlobalToasts();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { executionReportSub } = useExecutionReports();
  const { allowedSlippage } = useTradingSettings();
  const {
    enableExecutingOtherUserNegotiatedRFQ,
    truncateCareOrderID,
    requireMobileUITraderRole,
    sweepGroupIDQuotesOnlySweepable,
  } = useFeatureFlag();
  const isMobileLayout = useIsMobileLayout();

  const security = securitiesBySymbol.get(quote.Symbol);
  const disabledReason = getQuoteTradeSideDisabledReason({
    quote,
    quoteMarket: bestMarket,
    side,
    user,
    careOrderSide,
    isMobile: isMobileLayout,
    requireMobileUITraderRole,
    sweepGroupIDQuotesOnlySweepable,
    enableExecutingOtherUserNegotiatedRFQ,
  });
  const disabled = disabledReason != null;

  const { state, onClick, onReset } = useQuoteAcceptButtonState({
    quote,
    side,
    bestMarket,
    allowedSlippage: allowedSlippage?.value ? bpsToPercent(allowedSlippage.value) : undefined,
    security,
    disabled,
    truncateCareOrderID,
    webSocketClient,
    executionReportsSub: executionReportSub,
    addToast,
    isMobileLayout,
  });

  const isArmed = state === ACCEPT_BUTTON_STATE.Armed;
  const isLoading = state === ACCEPT_BUTTON_STATE.Loading;
  const label = side === SideEnum.Sell ? 'Sell' : 'Buy';
  const { execute: acceptQuoteWithConfirmation, dialog: acceptQuoteConfirmationDialog } = useRFQTradingConfirmation({
    quote,
    execute: onClick,
  });
  const handleClick = () => {
    if (isArmed) {
      acceptQuoteWithConfirmation();
      return;
    }

    onClick();
  };

  const variant = side === SideEnum.Sell ? ButtonVariants.Negative : ButtonVariants.Positive;

  return (
    <>
      <Box flex="1">
        <Tooltip tooltip={disabledReason} tooltipTestID={`quote-accept-button-${side.toLowerCase()}-disabled-tooltip`}>
          <Button
            size={FormControlSizes.Small}
            width="100%"
            variant={variant}
            disabled={disabled || isLoading}
            loading={isLoading}
            ref={createBlotterTableNativeClickRef<HTMLButtonElement>({
              onClick: handleClick,
              stopDoubleClickPropagation: true,
            })}
            onMouseLeave={onReset}
            data-status={state}
            data-testid={`quote-accept-button-${side.toLowerCase()}`}
          >
            {isArmed ? 'Confirm' : label}
          </Button>
        </Tooltip>
      </Box>
      {acceptQuoteConfirmationDialog}
    </>
  );
});
